<template>
  <div
    class="modal-overlay fixed inset-0 z-50 flex items-center justify-center bg-slate-900/55 p-4 backdrop-blur-[3px] sm:p-6"
    @click.self="close"
  >
    <div
      class="modal-content relative w-full max-w-lg overflow-hidden rounded-2xl border border-gray-200/90 bg-white shadow-2xl shadow-slate-900/10 ring-1 ring-black/[0.04] animate-scale-in dark:border-gray-700/90 dark:bg-gray-900 dark:shadow-black/40 dark:ring-white/[0.06]"
      @click.stop
    >
      <div
        class="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500"
        aria-hidden="true"
      />

      <header
        class="modal-header flex items-start gap-4 border-b border-gray-100 px-6 pb-4 pt-5 dark:border-gray-800"
      >
        <div
          class="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500/15 to-indigo-600/20 text-blue-600 dark:from-blue-400/20 dark:to-indigo-500/15 dark:text-blue-400"
        >
          <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
            />
          </svg>
        </div>
        <div class="min-w-0 flex-1 pt-0.5">
          <h2 class="modal-title text-lg font-semibold tracking-tight text-gray-900 dark:text-white">
            {{ t('kanban.createTask.title') }}
          </h2>
          <p class="mt-1 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
            {{ t('kanban.createTask.subtitle') }}
          </p>
        </div>
        <button
          type="button"
          class="modal-close shrink-0 rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-200"
          :aria-label="t('common.close')"
          @click="close"
        >
          <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </header>

      <form @submit.prevent="createTask" class="modal-form space-y-5 px-6 py-5">
        <div class="form-group space-y-2">
          <label class="form-label text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {{ t('kanban.createTask.nameLabel') }} <span class="text-rose-500">*</span>
          </label>
          <textarea
            ref="titleInput"
            v-model="form.titulo"
            class="form-input titulo-textarea"
            :placeholder="t('kanban.createTask.namePlaceholder')"
            required
            rows="1"
            @keydown.enter.exact.prevent="createTask"
            @input="autoResizeTituloCreate($event.target)"
            style="resize: none"
          />
        </div>

        <div
          class="modal-actions flex flex-col-reverse gap-2 border-t border-gray-100 bg-gray-50/60 px-0 pb-0 pt-5 dark:border-gray-800 dark:bg-gray-950/35 sm:flex-row sm:justify-end sm:gap-3 sm:bg-transparent sm:px-0 sm:pt-4"
        >
          <button
            type="button"
            class="btn-secondary inline-flex items-center justify-center rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700/90"
            @click="close"
          >
            {{ t('common.cancel') }}
          </button>
          <button
            type="submit"
            class="btn-primary inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition-all hover:from-blue-500 hover:to-indigo-500 hover:shadow-blue-500/30 disabled:pointer-events-none disabled:opacity-50"
            :disabled="loading || !form.titulo.trim()"
          >
            <span v-if="loading" class="loading-spinner" />
            {{ loading ? t('kanban.createTask.creating') : t('kanban.createTask.createButton') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import api from '@/services/api'
import Swal from 'sweetalert2'

export default {
  name: 'CreateTaskModal',
  props: {
    workspaceId: {
      type: Number,
      required: true
    },
    columns: {
      type: Array,
      default: () => []
    }
  },
  emits: ['close', 'created'],
  setup(props, { emit }) {
    const { t } = useI18n()
    const loading = ref(false)
    const titleInput = ref(null)

    const form = reactive({
      titulo: ''
    })

    const defaultColumn = computed(() => {
      if (props.columns.length === 0) return null
      const backlogColumn = props.columns.find(
        (col) => col.isPadrao || col.nome.toLowerCase().includes('backlog')
      )
      return backlogColumn || props.columns[0]
    })

    const autoResizeTituloCreate = (el) => {
      if (!el) return
      el.style.height = 'auto'
      const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 20
      const maxHeight = lineHeight * 5 + 12
      const newHeight = Math.min(el.scrollHeight, maxHeight)
      el.style.height = newHeight + 'px'
      el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden'
    }

    const close = () => {
      emit('close')
    }

    const createTask = async () => {
      try {
        loading.value = true

        const taskData = {
          titulo: form.titulo.trim(),
          workspaceId: props.workspaceId,
          colunaId: defaultColumn.value?.id || null,
          prioridade: 'MEDIA',
          status: 'PENDENTE'
        }

        console.log('Criando task:', taskData)

        const response = await api.post('/tasks', taskData)

        console.log('Task criada com sucesso:', response.data)

        emit('created', response.data)
        emit('close')
        form.titulo = ''
      } catch (error) {
        console.error('Erro ao criar task:', error)
        await Swal.fire({
          icon: 'error',
          title: t('kanban.createTask.errorTitle'),
          text: t('kanban.createTask.errorGeneric'),
          confirmButtonText: 'OK'
        })
      } finally {
        loading.value = false
      }
    }

    onMounted(() => {
      nextTick(() => {
        if (titleInput.value) {
          titleInput.value.focus()
        }
      })
    })

    return {
      t,
      loading,
      form,
      titleInput,
      close,
      createTask,
      autoResizeTituloCreate
    }
  }
}
</script>

<style scoped>
.titulo-textarea {
  overflow: hidden;
  line-height: 1.5;
  min-height: 2.75rem;
}

.form-input {
  width: 100%;
  padding: 0.625rem 0.875rem;
  background-color: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 0.75rem;
  color: var(--text-primary);
  font-size: 0.875rem;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    background-color 0.15s ease;
}

.form-input::placeholder {
  color: var(--text-muted);
}

.form-input:hover {
  border-color: var(--border-hover);
  background-color: var(--bg-hover);
}

.form-input:focus {
  outline: none;
  border-color: var(--info);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--info) 22%, transparent);
}

.loading-spinner {
  @apply h-4 w-4 shrink-0 rounded-full border-2 border-white border-t-transparent animate-spin;
}
</style>
